// Legacy ↔ HTML bridge for rich task content.
//
// Old descriptions/comments were stored as plain text with two tokens:
//   @Name                     → mention
//   ![alt](attachment:<id>)   → inline image
// New content is sanitized HTML (see sanitize.ts). We do NOT bulk-migrate the
// DB (strangler-fig): isLegacy() routes old rows to the old render path, and
// legacyToHtml() converts a row to HTML only when it's opened for editing, so
// it upgrades on the next save. No downtime, fully reversible.

interface StaffLite { id: string; name: string; active?: boolean; role?: string }

// A known formatting/structure tag means the value is already HTML. A stray
// "<" a user typed in old plain text (e.g. "x < y") won't match (needs a tag
// name), so it stays legacy. Worst case for the rare "a<b>c" literal is a
// cosmetic mis-render of one old row — acceptable per the no-migration choice.
const HTML_TAG_RE =
  /<(?:p|div|br|span|b|i|u|s|strike|del|mark|strong|em|ul|ol|li|img|a|blockquote)\b[^>]*>/i;

export function isLegacy(value: string | null | undefined): boolean {
  if (!value) return false;          // empty → nothing to convert
  return !HTML_TAG_RE.test(value);   // no known tag → still legacy token format
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Convert legacy token text to editor HTML. Mirrors renderRichText's token
 * grammar (longest-name-first, case-insensitive mentions) but emits an HTML
 * string. Everything between tokens is HTML-escaped so literal "<script>" in
 * an old note can't become live markup once it's treated as HTML.
 */
export function legacyToHtml(text: string, staff: StaffLite[]): string {
  if (!text) return '';
  const names = (staff || [])
    .filter(s => s.active !== false && s.role !== 'system' && s.name)
    .map(s => ({ id: s.id, name: s.name }))
    .sort((a, b) => b.name.length - a.name.length);
  const nameAlt = names.map(n => n.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const idByName = new Map(names.map(n => [n.name.toLowerCase(), n.id]));

  const combined = new RegExp(
    `!\\[([^\\]]*)\\]\\(attachment:([a-f0-9]{8,64})\\)` +
    (nameAlt ? `|@(${nameAlt})(?=$|[\\s,.!?，。！？、:;()])` : ''),
    'gui'
  );

  let out = '';
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = combined.exec(text)) !== null) {
    out += escapeHtml(text.slice(last, m.index));
    if (m[2]) {
      out += `<img data-attachment-id="${m[2]}" alt="${escapeHtml(m[1] || '')}">`;
    } else if (m[3]) {
      const id = idByName.get(m[3].toLowerCase()) ?? '';
      out += `<span class="mention" data-staff-id="${id}">@${escapeHtml(m[3])}</span>`;
    }
    last = m.index + m[0].length;
  }
  out += escapeHtml(text.slice(last));
  return out.replace(/\n/g, '<br>'); // legacy relied on white-space: pre-wrap
}
